// eslint-disable-next-line no-redeclare
import _ from 'lodash';
import { planById } from '@hotjar/webapp/domains/revenue/plan/utils';

/**
 * Services for Plan related permissions and comparisons.
 */
/**
 * @typedef {Object} Plan
 * @property {string} identifier The Plan ID
 * @property {number} sampleRate The sample rate for the plan
 * @property {string} featureGroup The features ("basic", "plus" or "business")
 */
angular.module('hjApp.plans').service('Plan', [
    '$window',
    'UserData',
    function ($window, UserData) {
        var self = this;

        // We don't like Python's `snake_case` in the frontend, so transform the
        // keys
        var camelCaseKeys = _.partial(_.mapKeys, _, function (value, key) {
            return _.camelCase(key);
        });

        var allOrderedPlans = _.map($window.settings.available_plans, camelCaseKeys);

        var orderedPlanIds = _.map(allOrderedPlans, 'identifier');

        /**************************
         * PUBLIC ACCESS FUNCTIONS
         **************************/
        /**
         * Return a Plan instance for a given ID
         *
         * @deprecated
         * @use If migrating to React, make use of the
         *      [usePlanRepository](packages/legacy/src/webapp/hooks/usePlanRepository.ts)
         *      hook, which returns an instance of PlanRepository. You can then call the `byId`
         *      method on the instance returned by the hook.
         *      If you are migrating an angular service/controller and you simply want to
         *      get rid of `Plan.byId`, you can replace it with the
         *      [planById](packages/legacy/src/webapp/domains/revenue/plan/utils/planById.ts) utility function.
         * @param {string} planId The plan ID being requested
         * @return {Plan|undefined} The matching plan, otherwise undefined
         */
        this.byId = planById;

        /**
         * Return a Plan instance for a given featureGroup
         *
         * The 'default' plan currently refers to the lowest occurrence in a
         * sorted list of plans, but this criteria may change in the future.
         *
         * @param {SitePlanInfo[]} plans An unsorted list of plans
         * @return {Plan|undefined} The default plan for a given feature group, otherwise undefined
         */
        this.defaultPlanForFeatureGroup = function defaultPlanForFeatureGroup(plans, featureGroup) {
            return _(plans).sortByAll(['featureGroup', 'sampleRate']).find({ featureGroup: featureGroup });
        };

        /**
         * Compare two plans and determine whether the change is a downgrade or upgrade.
         *
         * @param {string} newPlan The new plan being set.
         * @param {string} oldPlan The old plan.
         * @return {string|null} Either "upgrade" or "downgrade"
         */
        this.changeType = function changeType(newPlan, oldPlan) {
            var changeType = null;

            if (!newPlan || !oldPlan) {
                throw new Error(
                    'To compare two plans you need to specify two plans when using Plan.changeType: newPlan, oldPlan.'
                );
            }

            if ($window.settings.plans[newPlan].tier > $window.settings.plans[oldPlan].tier) {
                changeType = 'upgrade';
            } else if ($window.settings.plans[newPlan].tier < $window.settings.plans[oldPlan].tier) {
                changeType = 'downgrade';
            }

            return changeType;
        };

        /**
         * Return firstPlanId > secondPlanId
         * @param {string} firstPlanId The first plan to compare.
         * @param {string} secondPlanId The second plan to compare.
         * @returns {boolean}
         */
        this.gt = function gt(firstPlanId, secondPlanId) {
            return orderedPlanIds.indexOf(firstPlanId) > orderedPlanIds.indexOf(secondPlanId);
        };

        /**
         * Return firstPlanId < secondPlanId
         * @param {string} firstPlanId The first plan to compare.
         * @param {string} secondPlanId The second plan to compare.
         * @returns {boolean}
         */
        this.lt = function lt(firstPlanId, secondPlanId) {
            return firstPlanId !== secondPlanId && !self.gt(firstPlanId, secondPlanId);
        };

        /**
         * Return whichever plan is considered the 'maximum'. If they're equal
         * then the second plan is returned.
         * @param {string} firstPlanId The first plan to compare.
         * @param {string} secondPlanId The second plan to compare.
         * @returns {PlanId} The greater planId
         */
        this.max = function max(firstPlanId, secondPlanId) {
            if (self.gt(firstPlanId, secondPlanId)) return firstPlanId;
            return secondPlanId;
        };

        /**
         * Return an object which include all the info about a site's plan as well as the effective plan.
         * @param site Site to check for. Defaults to UserData.currentSite.
         */
        this.get = function get(site) {
            site = site || UserData.currentSite;

            // We might not have a site available such as when the user might not be logged in.
            if (!site) {
                return false;
            }

            // NOTE: The "plan" is the plan we have stored in our database – the plan the user has set.
            //       The "effective" plan is the plan in effect. This is determined by checking that the billing
            //       date for the plan's account is in the future, not the past.
            //       If this is the case, the "effective" plan would default to "basic".
            //
            // When it comes to the actual plan, we generally only care about "effective" in the interface,
            // Except for a few cases – such as the billing page.

            // Note: In the case of billing v1, the site plan info will be propagated with the organizations plan info.
            return {
                alwaysOnRate: site.always_on_rate,
                name: site.plan,
                sampleRate: site.sample_rate,
                effective: {
                    name: site.effective_plan,
                    alwaysOnRate: site.always_on_rate,
                    sampleRate: site.sample_rate,
                    feedback_plan: site.effective_plan_for_feedback
                }
            };
        };

        /**
         * Returns the highest plan for either the user or the account.
         * @param parent Can be either 'user' or 'account'.
         * @param obj Used in case we want to retrieve the highest plan for a user, specific account or specific
         *            organization.
         */
        this.highest = function highest(parent, obj) {
            if (_.isUndefined(parent)) {
                throw new Error(
                    'Plan.highest(): You need to specify for which parent the highest ' +
                        'plan is needed: "user" or "account" or "organization".'
                );
            }

            if (parent === 'account') {
                return getHighestPlanForAccount('plan', obj);
            } else if (parent === 'organization') {
                return getHighestPlanForOrganization('plan', obj);
            }
        };

        /*******************
         * PRIVATE FUNCTIONS
         *******************/

        /**
         * Returns the highest plan for an account.
         * @param planKey Which plan do we want: plan | effective_plan
         * @param account Account to retrieve highest plan for.
         */
        function getHighestPlanForAccount(planKey, account) {
            if (_.isUndefined(account)) {
                throw new Error('Plan.highest(): You must specify (pass) an account.');
            }

            var fullPlanKey = 'highest_' + planKey;

            return account[fullPlanKey];
        }

        /**
         * Returns the highest plan for an organization.
         * @param planKey Which plan do we want: plan | effective_plan
         * @param organization Organization to retrieve highest plan for.
         */
        function getHighestPlanForOrganization(planKey, organization) {
            var items;

            if (_.isUndefined(organization)) {
                throw new Error('Plan.highest(): You must specify (pass) an account.');
            }

            items = organization.sites;

            return getHighestPlanTypeFromList(items, planKey)[planKey];
        }

        /**
         * Returns the highest plan available in a list of objects.
         * @param items The items containing plan details.
         * @param planKey Which plan do we want: plan | effective_plan
         */
        function getHighestPlanTypeFromList(items, planKey) {
            return _.max(items, function (item) {
                return window.settings.plans[item[planKey]].tier;
            });
        }
    }
]);
